import Popup from '../../../../components/Popup/Popup'
import Button from '../../../../components/Button/Button'
import { BUTTON_TYPES } from '../../../../components/Button'
import { BTN_TXT, LABELS } from '../../../../constants'
import { useSplitFeesPopup } from '../../SplitFees/Providers/SplitFeesPopupProvider'
import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { FieldWithLabel } from '../../../../components/FieldWithLabel/FieldWithLabel'
import { Input } from '../../../../components/Input'
import { percentageParser } from '../../../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { ReactComponent as ChevronDown16 } from '../../../../assets/svg/ChevronDown16.svg'
import { ReactComponent as ChevronUp16 } from '../../../../assets/svg/ChevronUp16.svg'
import { TFormEditFees } from './types'
import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import { useLedByGPOListContext } from './LedByGPOContextProvider'
import { validateMessages } from '../../../../helper/common'
import { getUser } from '../../../../redux/store/user/getters'
import { useFinancialsAbility } from '../../Providers/FinancialsAbilityProvider'
import { ACTIONS, contractSubject, SUBJECTS } from '../../Abilities'

export const EditFeesPopup = () => {
  const {
    editLedByGPOPopup: { actions, state }
  } = useSplitFeesPopup()

  const {
    actions: { editFees }
  } = useLedByGPOListContext()

  const {
    payload: { selectedRow }
  } = state

  const [form] = Form.useForm<TFormEditFees>()
  const user = useSelector(getUser)
  const ability = useFinancialsAbility()
  const canSeeCommunitySplit = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        contractSubject(SUBJECTS.GPO_COMMUNITY_DATA, user)
      ),
    [ability, user]
  )
  const showCommunitySplit =
    canSeeCommunitySplit && !!selectedRow?.is_shared_with_community

  const onEditFees = useCallback(
    (values) => {
      if (!selectedRow) return
      editFees(selectedRow.key, {
        ...values,
        community_split: canSeeCommunitySplit ? values.community_split : ''
      })
    },
    [editFees, selectedRow, canSeeCommunitySplit]
  )

  const onHSSplitChange = useCallback(
    (value) => {
      form.setFieldsValue({
        community_split: String((100 - Number(value)).toFixed(2))
      })
    },
    [form]
  )

  const onCOSplitChange = useCallback(
    (value) => {
      form.setFieldsValue({
        hs_split: String((100 - Number(value)).toFixed(2))
      })
    },
    [form]
  )

  const onCancel = useCallback(() => {
    actions.close()
    form.resetFields()
  }, [actions, form])

  useEffect(() => {
    if (selectedRow && state.visible)
      form.setFieldsValue({
        gpo_retain: selectedRow.gpo_retain ?? undefined,
        community_split: showCommunitySplit
          ? selectedRow.community_split ?? undefined
          : undefined,
        hs_split: showCommunitySplit
          ? selectedRow.hs_split ?? undefined
          : undefined
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow, state.visible, showCommunitySplit])

  if (!selectedRow) return null

  return (
    <Popup
      visible={state.visible}
      onCancel={onCancel}
      title={state.title}
      width={440}
      footer={
        <>
          <Button type={BUTTON_TYPES.DEFAULT} onClick={onCancel} upperCase>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            form="edit-fees-led-by-gpo"
            htmlType="submit"
            upperCase
          >
            {BTN_TXT.SAVE}
          </Button>
        </>
      }
    >
      <CustomForm<TFormEditFees>
        id="edit-fees-led-by-gpo"
        form={form}
        onFinish={onEditFees}
        validateMessages={validateMessages}
      >
        <div className="column gap-24">
          <FieldWithLabel
            title={canSeeCommunitySplit ? 'CO&HS name' : 'HS name'}
          >
            {selectedRow.health_system_name}
          </FieldWithLabel>
          <Input.Number
            className="spinner-input-number"
            propsItem={{
              name: 'gpo_retain',
              label: LABELS.GPO_ALLOCATION,
              rules: [{ required: true }]
            }}
            propsInputNumber={{
              controls: {
                downIcon: <ChevronDown16 />,
                upIcon: <ChevronUp16 />
              },
              min: '0',
              max: '100',
              parser: percentageParser,
              placeholder: '0'
            }}
          />
          {showCommunitySplit && (
            <div className="row gap-10">
              <Input.Number
                className="spinner-input-number"
                propsItem={{
                  name: 'community_split',
                  label: LABELS.CO_RETAIN,
                  rules: [{ required: true }]
                }}
                propsInputNumber={{
                  controls: {
                    downIcon: <ChevronDown16 />,
                    upIcon: <ChevronUp16 />
                  },
                  min: '0',
                  max: '100',
                  parser: percentageParser,
                  placeholder: '0',
                  onChange: onCOSplitChange
                }}
              />
              <Input.Number
                className="spinner-input-number"
                propsItem={{
                  name: 'hs_split',
                  label: LABELS.HS_SPLIT,
                  rules: [{ required: true }]
                }}
                propsInputNumber={{
                  controls: {
                    downIcon: <ChevronDown16 />,
                    upIcon: <ChevronUp16 />
                  },
                  min: '0',
                  max: '100',
                  parser: percentageParser,
                  placeholder: '0',
                  onChange: onHSSplitChange
                }}
              />
            </div>
          )}
        </div>
      </CustomForm>
    </Popup>
  )
}
